<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { PROFILE_KEYS, ATTENTION_KEYS } from './advisorReportHelpers';

const props = defineProps({
  modelValue: { type: Object, required: true },
  agents: { type: Array, default: () => [] },
  inboxes: { type: Array, default: () => [] },
  teams: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
  busy: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue', 'apply', 'reset']);
const { t } = useI18n();
const expanded = ref(false);
const update = (key, value) =>
  emit('update:modelValue', { ...props.modelValue, [key]: value });
const selectFields = computed(() => [
  {
    key: 'agent_profile',
    options: PROFILE_KEYS.map(id => ({
      id,
      name: t(`ADVISOR_REPORT.PROFILES.${id}`),
    })),
  },
  { key: 'author_id', options: props.agents },
  {
    key: 'assignee_id',
    options: [
      { id: 'unassigned', name: t('ADVISOR_REPORT.PROFILES.unassigned') },
      ...props.agents,
    ],
  },
  { key: 'inbox_id', options: props.inboxes },
  { key: 'team_id', options: props.teams },
  {
    key: 'label',
    options: props.labels.map(label => ({
      id: label.title,
      name: label.title,
    })),
  },
  {
    key: 'attention_status',
    options: ATTENTION_KEYS.map(id => ({
      id,
      name: t(`ADVISOR_REPORT.ATTENTION.${id}`),
    })),
  },
  {
    key: 'status',
    options: ['open', 'pending', 'snoozed'].map(id => ({
      id,
      name: t(`ADVISOR_REPORT.STATUS.${id}`),
    })),
  },
]);
const visibleFields = computed(() =>
  expanded.value
    ? selectFields.value
    : selectFields.value.filter(field =>
        ['agent_profile', 'assignee_id'].includes(field.key)
      )
);
</script>

<template>
  <form
    class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
    @submit.prevent="emit('apply')"
  >
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      <label
        v-for="key in ['since_date', 'until_date']"
        :key="key"
        class="flex flex-col gap-1.5 mb-0 text-xs font-medium text-n-slate-11"
      >
        <span>{{ t(`ADVISOR_REPORT.FILTERS.${key}`) }}</span>
        <input
          class="w-full h-9 min-w-0 mb-0 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 text-sm font-normal"
          :value="modelValue[key]"
          type="date"
          required
          :aria-label="t(`ADVISOR_REPORT.FILTERS.${key}`)"
          @input="update(key, $event.target.value)"
        />
      </label>
      <label
        v-for="field in visibleFields"
        :key="field.key"
        class="flex flex-col gap-1.5 mb-0 text-xs font-medium text-n-slate-11"
      >
        <span>{{ t(`ADVISOR_REPORT.FILTERS.${field.key}`) }}</span>
        <select
          class="w-full h-9 min-w-0 mb-0 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 text-sm font-normal"
          :value="modelValue[field.key]"
          :aria-label="t(`ADVISOR_REPORT.FILTERS.${field.key}`)"
          @change="update(field.key, $event.target.value)"
        >
          <option value="">{{ t('ADVISOR_REPORT.ALL') }}</option>
          <option
            v-for="option in field.options"
            :key="option.id"
            :value="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </label>
      <label
        v-if="expanded"
        class="flex flex-col gap-1.5 mb-0 text-xs font-medium text-n-slate-11"
      >
        <span>{{ t('ADVISOR_REPORT.FILTERS.min_pending_hours') }}</span>
        <input
          class="w-full h-9 min-w-0 mb-0 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 text-sm font-normal"
          :value="modelValue.min_pending_hours"
          type="number"
          min="0"
          step="0.5"
          :aria-label="t('ADVISOR_REPORT.FILTERS.min_pending_hours')"
          :placeholder="String(0)"
          @input="update('min_pending_hours', $event.target.value)"
        />
      </label>
      <label
        v-if="expanded"
        class="flex flex-col gap-1.5 mb-0 text-xs font-medium text-n-slate-11"
      >
        <span>{{ t('ADVISOR_REPORT.FILTERS.query') }}</span>
        <input
          class="w-full h-9 min-w-0 mb-0 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 text-sm font-normal"
          :value="modelValue.query"
          type="search"
          maxlength="100"
          :placeholder="t('ADVISOR_REPORT.SEARCH_PLACEHOLDER')"
          @input="update('query', $event.target.value)"
        />
      </label>
    </div>
    <div class="flex flex-wrap justify-between items-center gap-3 mt-4">
      <Button
        type="button"
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-sliders-horizontal"
        :aria-expanded="expanded"
        :label="
          t(
            expanded
              ? 'ADVISOR_REPORT.LESS_FILTERS'
              : 'ADVISOR_REPORT.MORE_FILTERS'
          )
        "
        @click="expanded = !expanded"
      />
      <p class="text-xs text-n-slate-11 m-0 flex-1 min-w-48">
        {{ t('ADVISOR_REPORT.FILTER_HINT') }}
      </p>
      <Button
        type="button"
        variant="ghost"
        color="slate"
        :label="t('ADVISOR_REPORT.RESET')"
        :disabled="busy"
        @click="emit('reset')"
      />
      <Button
        type="submit"
        icon="i-lucide-list-filter"
        :label="t('ADVISOR_REPORT.APPLY')"
        :is-loading="busy"
        :disabled="busy"
      />
    </div>
  </form>
</template>
